/**
 * boss-communication — 视频配置
 *
 * 结构：
 *   S01 — 开场：个人出镜 + 关键词逐次淡入 + 字幕
 *   S02 — 第一步演示：建立档案（全屏录屏 + 字幕）
 *   S03 — 第二步演示：档案使用（全屏录屏 + 字幕）
 *   S04 — 结尾 CTA：个人出镜 + GitHub 截图 + 故障标题 + 字幕
 */

import type { BgmConfig, SceneConfig, SubtitleSegment } from "../lib/types";

// ── 时长（帧，30fps）──
const S01_DUR = 601;  // ~20.04s
const S02_DUR = 2062; // ~68.74s
const S03_DUR = 1323; // ~44.11s
const S04_DUR = 450;  // ~15s

// ── 字幕数据（场景相对时间，秒）──

const s01_subtitles: SubtitleSegment[] = [
  { start: 0.0, end: 5.86, text: "日常工作中，你跟同事、特别是跟老板沟通的时候，是不是也经常觉得挺费劲的？" },
  { start: 5.86, end: 8.38, text: "毕竟每个人的性格、沟通方式都不一样。" },
  { start: 8.38, end: 11.79, text: "今天分享给你一个巨好用、百试不爽的 skills。" },
  { start: 11.78, end: 17.78, text: "它已经帮我省下了无数沟通内耗，并且我也把它分享给我的朋友用了，只要用过的都说好用！" },
  { start: 17.78, end: 20.04, text: "它的逻辑很简单，主要分两步：" },
];

const s02_subtitles: SubtitleSegment[] = [
  { start: 0.0, end: 4.68, text: "第一步，就是和 AI 聊天，来形成一份针对老板的专属沟通档案。" },
  { start: 4.68, end: 7.63, text: "在这个过程中，和 AI 聊聊八卦就可以了。" },
  { start: 7.63, end: 10.61, text: "比如说，你对老板的评价是什么样子的。" },
  { start: 10.61, end: 12.51, text: "你对他的第一印象是什么。" },
  { start: 12.5, end: 14.01, text: "你照常回答就可以。" },
  { start: 14.02, end: 17.55, text: "AI 也会根据你的回答，让你做一些小场景的选择题。" },
  { start: 17.54, end: 19.8, text: "它会根据你的回复去做一些剖析。" },
  { start: 19.8, end: 22.06, text: "然后可能也会帮你做一些预测。" },
  { start: 22.06, end: 24.94, text: "根据他的预测，你可以看看他说的准不准。" },
  { start: 24.94, end: 26.72, text: "然后提供一些进一步的信息。" },
  { start: 26.71, end: 28.41, text: "AI 也会进一步追问一些细节。" },
  { start: 28.42, end: 30.65, text: "如果你回答不上来，那也没有关系。" },
  { start: 30.65, end: 32.67, text: "AI 会向你问一些别的问题。" },
  { start: 32.66, end: 36.67, text: "当然，我们在回答的时候，难免会夹杂一些个人的情绪。" },
  { start: 36.67, end: 39.05, text: "AI 也可以帮你把一些主观的情绪识别出来。" },
  { start: 39.05, end: 40.99, text: "形成一些更客观的描述。" },
  { start: 40.99, end: 43.73, text: "有利于形成一份更加准确的个人档案。" },
  { start: 43.73, end: 47.67, text: "在完成全部这些事情之后，我们也会把一些主观的情绪识别出来。" },
  { start: 47.66, end: 51.96, text: "在完成全部这些事情之后，AI 就可以帮你做一份专属的深度档案。" },
  { start: 51.96, end: 55.66, text: "里面包括了对这个老板的人格标签，各种人格的框架。" },
  { start: 55.66, end: 59.38, text: "它会进行识别分析，同时也会记录一些实际的案例。" },
  { start: 59.38, end: 62.43, text: "以及应对老板的一些语言风格和沟通的雷区。" },
  { start: 62.42, end: 64.03, text: "以及他个人的情绪特点。" },
  { start: 64.03, end: 66.72, text: "如果你后续有新的案例、新的事情发生。" },
  { start: 66.72, end: 68.74, text: "也可以和 AI 一起更新这个档案。" },
];

const s03_subtitles: SubtitleSegment[] = [
  { start: 0.0, end: 13.87, text: "在建立好个人档案之后，后面遇到具体的事，比如方案设计、绩效沟通，你都可以调用这个 Skills 载入老板的档案，输入你的具体需求和问题，让 AI 充当你的智囊团，制定专属的沟通方案。" },
  { start: 13.87, end: 22.77, text: "AI 会针对你的问题，告诉你更合理的行为措施，并且告诉你背后的逻辑——根据他的档案和性格特点，拆解背后的这个逻辑。" },
  { start: 22.78, end: 30.17, text: "告诉你侧重点在哪里、应该凸显什么内容，直接告诉你和老板沟通的具体话术，你直接照抄就可以，安全感满满。" },
  { start: 30.17, end: 35.38, text: "你还可以基于可能发生的情况去追问，AI 可以和你一起探讨，制定应对的方案。" },
  { start: 35.38, end: 40.06, text: "然后告诉你应该避开的雷区、更合理的结构、更契合老板的心意。" },
  { start: 40.06, end: 44.12, text: "让你在整个沟通过程中，能够避开雷区，又能全面应对。" },
];

const s04_subtitles: SubtitleSegment[] = [
  { start: 0.0, end: 5.59, text: "不管是直接沟通、聊天软件发消息，还是要通过汇报提交材料，它都能帮你搞定。" },
  { start: 5.59, end: 8.01, text: "你直接照着做、抄答案就行！" },
  { start: 8.02, end: 10.54, text: "我跟你说，真的百试不爽！" },
  { start: 10.54, end: 15.0, text: "现在我已经把这个 skills 整理好了，放到了评论区，赶紧自取试试吧！" },
];

// ── 场景配置 ──

export const bossCommunicationConfig: SceneConfig[] = [
  // ════════════════════════════════════════════
  // S01 — 开场：出镜 + 关键词淡入
  // ════════════════════════════════════════════
  {
    sceneId: "S01",
    durationInFrames: S01_DUR,
    layers: [
      // 底层：出镜视频背景
      {
        component: "VideoBackground",
        preset: "muted",
        props: {
          src: "video/personal-appearance.mp4",
        },
      },
      // 关键词 1：沟通费劲？ (0s 出现，hold 4s 后淡出)
      {
        component: "SmashText",
        preset: "fadeIn",
        props: {
          mainText: "沟通费劲？",
          style: {
            holdDuration: 120,
          },
        },
      },
      // 关键词 2：性格不同 (5s 出现)
      {
        component: "SmashText",
        preset: "fadeIn",
        props: {
          mainText: "性格不同",
          style: {
            holdDuration: 70,
          },
        },
        offset: 150,
      },
      // 关键词 3：巨好用 (8s 出现)
      {
        component: "SmashText",
        preset: "fadeIn",
        props: {
          mainText: "巨好用",
          style: {
            holdDuration: 70,
          },
        },
        offset: 240,
      },
      // 关键词 4：省下沟通内耗 (11s 出现)
      {
        component: "SmashText",
        preset: "fadeIn",
        props: {
          mainText: "省下沟通内耗",
          style: {
            holdDuration: 100,
          },
        },
        offset: 330,
      },
      // 关键词 5：主要分两步 (16s 出现，保留到结束)
      {
        component: "SmashText",
        preset: "fadeIn",
        props: {
          mainText: "主要分两步",
          style: {
            holdDuration: 9999,
          },
        },
        offset: 480,
      },
      // 底部字幕
      {
        component: "TimedSubtitle",
        preset: "static",
        props: {
          data: s01_subtitles,
        },
      },
    ],
    audio: { src: "audio/tts-S01.mp3" },
  },

  // ════════════════════════════════════════════
  // S02 — 第一步演示：建立档案（录屏 + 字幕）
  // ════════════════════════════════════════════
  {
    sceneId: "S02",
    durationInFrames: S02_DUR,
    transition: {
      type: "fade",
      durationInFrames: 30,
    },
    layers: [
      {
        component: "VideoBackground",
        preset: "contain",
        props: {
          src: "video/step1-demo.mp4",
        },
      },
      {
        component: "TimedSubtitle",
        preset: "static",
        props: {
          data: s02_subtitles,
        },
      },
    ],
    audio: { src: "audio/tts-S02.mp3" },
  },

  // ════════════════════════════════════════════
  // S03 — 第二步演示：档案使用（录屏 + 字幕）
  // ════════════════════════════════════════════
  {
    sceneId: "S03",
    durationInFrames: S03_DUR,
    transition: {
      type: "dissolve",
      durationInFrames: 30,
    },
    layers: [
      {
        component: "VideoBackground",
        preset: "contain",
        props: {
          src: "video/step2-demo.mp4",
        },
      },
      {
        component: "TimedSubtitle",
        preset: "static",
        props: {
          data: s03_subtitles,
        },
      },
    ],
    audio: { src: "audio/tts-S03.mp3" },
  },

  // ════════════════════════════════════════════
  // S04 — 结尾 CTA：出镜 + 截图 + 标题
  // ════════════════════════════════════════════
  {
    sceneId: "S04",
    durationInFrames: S04_DUR,
    transition: {
      type: "fade",
      durationInFrames: 30,
    },
    layers: [
      // 底层：出镜视频背景
      {
        component: "VideoBackground",
        preset: "muted",
        props: {
          src: "video/personal-appearance.mp4",
          style: {
            startFrom: 620, // S01 用了前 601 帧，S04 从 620 帧开始播后半段
          },
        },
      },
      // GitHub 截图：从下方浮入，60% 屏幕宽度，透出背景
      {
        component: "ImageShow",
        preset: "floatIn",
        props: {
          src: "github_screenshot.png",
          style: {
            scalePercent: 0.6,
            objectFit: "contain",
            overlayOpacity: 0,
            backgroundColor: "transparent",
          },
        },
        offset: 60, // 旁白说到"聊天软件发消息"时出现
      },
      // 故障标题 CTA
      {
        component: "SmashText",
        preset: "glitch",
        props: {
          mainText: "评论区自取",
          subText: "真的百试不爽！",
        },
        offset: 240, // 旁白末尾"放到了评论区"时弹出
      },
      // 底部字幕
      {
        component: "TimedSubtitle",
        preset: "static",
        props: {
          data: s04_subtitles,
        },
      },
    ],
    audio: { src: "audio/tts-S04.mp3" },
  },
];

// ── 背景音乐配置 ──

export const bossCommunicationBgm: BgmConfig = {
  src: "bgm.mp3",
  volume: 0.15,
  loop: true,
};
;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
